<template>
  <div class="payer-orders">
    <div class="d-flex align-items-start justify-content-between payer-header">
      <div class="payer-name">
        <PayerName
          :name="payer.name"
          :type="payer.type"
        />
      </div>
      <button
        v-if="braceletProperty"
        type="button"
        class="bracelet-btn"
        @click="braceletOpen = true"
      >
        <i class="fa-solid fa-key pr-2" />
        {{ hasBracelet ? $t('shop.todayOrder.bracelet.change') : $t('shop.todayOrder.bracelet.add') }}
      </button>
    </div>

    <BraceletAssignModal
      v-model="braceletOpen"
      :payer-id="payer.id"
      @assigned="$emit('bracelet-assigned', $event)"
    />

    <div
      v-if="payerProperties.length > 0"
      class="payer-properties"
    >
      <div
        v-for="p in payerProperties"
        :key="p.code"
      >
        <span class="property-name">{{ p.name }}:</span>
        {{ p.value }}
      </div>
    </div>

    <hr class="divider">

    <!-- Edit mode of the simple variant: modify existing orders and order more -->
    <div
      v-if="editing && !fastEdit"
      class="edit-box"
    >
      <div class="section-title">
        {{ $t('shop.todayOrder.ordered') }}:
      </div>
      <OrderRow
        v-for="o in orders"
        :key="o.availabilityId"
        v-model="counts[o.availabilityId]"
        :name="o.name"
        :price="o.unitPrice ? perPiece(o.unitPrice, o.currency) : null"
        :crossed="counts[o.availabilityId] === 0"
        :changed="counts[o.availabilityId] !== o.count"
      />

      <template v-if="newItems.length > 0">
        <hr class="divider">
        <div class="section-title">
          {{ $t('shop.todayOrder.orderMore') }}:
        </div>
        <OrderRow
          v-for="l in newItems"
          :key="l.id"
          v-model="counts[l.id]"
          :name="l.name"
          :price="perPiece(l.price, l.currency)"
          :changed="counts[l.id] > 0"
        />
      </template>

      <div class="actions">
        <button
          type="button"
          class="action-btn cancel"
          :disabled="pending"
          @click="cancelEdit"
        >
          <i class="fa-regular fa-xmark pr-2" />
          {{ $t('general.cancel') }}
        </button>
        <button
          type="button"
          class="action-btn confirm"
          :disabled="pending || !hasChanges"
          @click="save"
        >
          <i
            v-if="pending"
            class="fas fa-spinner fa-spin pr-2"
          />
          <i
            v-else
            class="fa-regular fa-check pr-2"
          />
          {{ $t('general.confirm') }}
        </button>
      </div>
    </div>

    <template v-else>
      <div class="section-title">
        {{ $t('shop.todayOrder.toDeliver') }}:
      </div>

      <div
        v-if="orders.length === 0"
        class="no-orders"
      >
        <i class="fa-light fa-empty-set" />
        <template v-if="payerOffer.length === 0">
          <div class="font-weight-bold">
            {{ $t('shop.todayOrder.noOrdersTitle') }}
          </div>
          <div class="no-offer-info">
            {{ $t('shop.todayOrder.noOfferInfo') }}
          </div>
        </template>
        <div v-else>
          {{ $t('shop.noOrdersForToday') }}.
        </div>
      </div>

      <div
        v-for="order in orders"
        :key="order.availabilityId"
        class="product-set"
        :class="{ changed: fastEdit && counts[order.availabilityId] !== order.count }"
      >
        <!-- Fast edit: quantities are editable right away -->
        <template v-if="fastEdit">
          <div class="d-flex align-items-center product-set-header">
            <div
              class="product-set-name flex-grow-1"
              :class="{ crossed: counts[order.availabilityId] === 0 }"
            >
              {{ order.name }}
            </div>
            <CountStepper
              :value="counts[order.availabilityId] ?? order.count"
              @input="$set(counts, order.availabilityId, $event)"
            />
          </div>
          <div class="d-flex flex-wrap statuses">
            <div
              class="status"
              :class="order.isPaid ? 'green' : 'red'"
            >
              <i class="fa-solid fa-dollar-sign pr-2" />
              {{ order.isPaid ? $t('shop.paid') : $t('shop.notPaid') }}
            </div>
            <div
              class="status"
              :class="order.delivered <= 1 ? 'green' : 'red'"
            >
              <i class="fa-solid fa-hand-holding-box pr-2" />
              {{ order.delivered <= 1 ? $t('shop.delivery.first') : $t('shop.delivery.next', [order.delivered]) }}
            </div>
          </div>
          <hr
            v-if="order.products.length > 0"
            class="product-set-divider"
          >
        </template>
        <div
          v-else
          class="product-set-name"
        >
          <span v-if="order.count > 1">{{ order.count }}x</span>
          {{ order.name }}
        </div>

        <div
          v-for="p in order.products"
          :key="p.id"
          class="product"
        >
          <div class="product-tag">
            {{ p.name }}
          </div>
          <div
            v-if="showMealTitle && (p.planTitle || p.description)"
            class="product-description"
          >
            {{ p.planTitle || p.description }}
          </div>
          <div
            v-if="showMealTitle && p.planDescription"
            class="product-plan-description"
          >
            {{ p.planDescription }}
          </div>
        </div>

        <div
          v-if="!fastEdit"
          class="d-flex flex-wrap statuses"
        >
          <div
            class="status"
            :class="order.isPaid ? 'green' : 'red'"
          >
            <i class="fa-solid fa-dollar-sign pr-2" />
            {{ order.isPaid ? $t('shop.paid') : $t('shop.notPaid') }}
          </div>
          <div
            class="status"
            :class="order.delivered <= 1 ? 'green' : 'red'"
          >
            <i class="fa-solid fa-hand-holding-box pr-2" />
            {{ order.delivered <= 1 ? $t('shop.delivery.first') : $t('shop.delivery.next', [order.delivered]) }}
          </div>
        </div>
      </div>

      <!-- Order directly: always in fast edit, otherwise only when there are no orders yet -->
      <template v-if="(fastEdit || orders.length === 0) && newItems.length > 0">
        <hr class="divider">
        <div class="section-title">
          {{ orders.length > 0 ? $t('shop.todayOrder.orderMore') : $t('shop.todayOrder.order') }}:
        </div>
        <OrderRow
          v-for="l in newItems"
          :key="l.id"
          v-model="counts[l.id]"
          :name="l.name"
          :price="perPiece(l.price, l.currency)"
          :changed="counts[l.id] > 0"
        />
      </template>

      <!-- Shown once any quantity changes -->
      <div
        v-if="hasChanges"
        class="actions"
      >
        <button
          type="button"
          class="action-btn cancel"
          :disabled="pending"
          @click="resetCounts"
        >
          <i class="fa-regular fa-xmark pr-2" />
          {{ $t('general.cancel') }}
        </button>
        <button
          type="button"
          class="action-btn confirm"
          :disabled="pending"
          @click="save"
        >
          <i
            v-if="pending"
            class="fas fa-spinner fa-spin pr-2"
          />
          <i
            v-else
            class="fa-regular fa-check pr-2"
          />
          {{ $t('general.confirm') }}
        </button>
      </div>

      <div
        v-if="!fastEdit && orders.length > 0"
        class="actions"
      >
        <button
          type="button"
          class="action-btn edit w-100"
          @click="startEdit"
        >
          <i class="fa-solid fa-pen pr-2" />
          {{ $t('general.edit') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import PayerName from '@/components/payers/PayerName';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import BraceletAssignModal from './today/BraceletAssignModal';
import CountStepper from './today/CountStepper';
import OrderRow from './today/OrderRow';

export default {
  props: {
    payer: Object,
    orderData: Object,
    catalogId: String,
    // Listings available today
    offer: {
      type: Array,
      default: () => [],
    },
    // Today's delivery plan entries, with menuId of the menu node they belong to
    deliveryPlan: {
      type: Array,
      default: () => [],
    },
    // Diet nodes of the catalog
    diets: {
      type: Array,
      default: () => [],
    },
    // Quantities of all orders are editable without entering the edit mode
    fastEdit: Boolean,
    // Payer property storing the bracelet id, assigning bracelets is off when empty
    braceletProperty: String,
    // Payer properties to show below the name
    properties: {
      type: Array,
      default: () => [],
    },
    // When off, only the listing name and product tags are shown
    showMealTitle: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    editing: false,
    braceletOpen: false,
    pending: false,
    counts: {},
    payerListingIds: null,
  }),
  components: {
    PayerName,
    OrderRow,
    CountStepper,
    BraceletAssignModal,
  },
  computed: {
    ...mapGetters(['formatCurrency']),
    hasBracelet() {
      return !!(this.payer.properties || {})[this.braceletProperty];
    },
    payerProperties() {
      const values = this.payer.properties || {};
      // Rows for missing values are kept, so the block doesn't change height between payers
      return this.properties
        .map((p) => ({ ...p, value: values[p.code] || '-' }));
    },
    orders() {
      if (!this.orderData || !this.orderData.orders) return [];
      return this.orderData.orders
        .filter((x) => x.payerId === this.payer.id)
        .reduce((acc, curr) => {
          const existing = acc.find((x) => x.availabilityId === curr.availabilityId);
          if (existing) {
            existing.count += curr.count;
            // Each row counts how many times the payer was served, not items
            existing.delivered = Math.max(existing.delivered, curr.delivered);
            existing.isPaid = existing.isPaid && curr.isPaid;
          } else {
            acc.push({ ...curr });
          }
          return acc;
        }, [])
        .map((o) => {
          const listing = this.offer.find((l) => l.id === o.availabilityId);
          const productSet = (this.orderData.productSets || []).find((x) => x.id === o.productSetId);
          // Listing is assigned to menu and diet nodes, plan entries are defined per menu, diet and product
          const nodeIds = (listing?.assignments || []).map((a) => a.nodeId);
          const products = (listing?.productIds || [])
            .map((id) => (this.orderData.products || []).find((p) => p.id === id))
            .filter((p) => p)
            .map((p) => {
              const dietId = this.orderDietId(nodeIds);
              const entry = this.deliveryPlan.find((e) => e.productId === p.id
                && e.day === o.day
                && e.dietId === dietId
                && nodeIds.includes(e.menuId));

              return {
                ...p,
                planTitle: entry?.title,
                planDescription: entry?.description,
              };
            });

          return {
            ...o,
            name: o.listingName || productSet?.name || listing?.name,
            products,
          };
        });
    },
    payerOffer() {
      if (!this.payerListingIds) return this.offer;
      return this.offer.filter((l) => this.payerListingIds.includes(l.id));
    },
    newItems() {
      const orderedIds = this.orders.map((o) => o.availabilityId);
      return this.payerOffer.filter((l) => !orderedIds.includes(l.id));
    },
    addedItems() {
      const fromOrdered = this.orders
        .filter((o) => (this.counts[o.availabilityId] ?? o.count) > o.count)
        .map((o) => ({
          day: o.day,
          payerId: this.payer.id,
          availabilityId: o.availabilityId,
          count: this.counts[o.availabilityId] - o.count,
        }));

      const fromNew = this.newItems
        .filter((l) => this.counts[l.id] > 0)
        .map((l) => ({
          day: moment().format('YYYY-MM-DD'),
          payerId: this.payer.id,
          availabilityId: l.id,
          count: this.counts[l.id],
        }));

      return fromOrdered.concat(fromNew);
    },
    cancelledItems() {
      return this.orders
        .filter((o) => (this.counts[o.availabilityId] ?? o.count) < o.count)
        .map((o) => ({
          day: o.day,
          payerId: this.payer.id,
          availabilityId: o.availabilityId,
          count: o.count - this.counts[o.availabilityId],
        }));
    },
    hasChanges() {
      return this.addedItems.length > 0 || this.cancelledItems.length > 0;
    },
    // Orders are re-created on every data refresh, compare only what matters for the counts
    ordersKey() {
      return this.orders.map((o) => `${o.availabilityId}:${o.count}`).join(',');
    },
  },
  watch: {
    ordersKey() {
      this.resetCounts();
    },
    offer() {
      this.requestPayerOffer();
      this.resetCounts();
    },
  },
  methods: {
    ...mapActions('shop', [
      'reportDelivery',
      'reportSingleDelivery',
      'getOrders',
      'findPayersOffer',
      'updateOrders',
    ]),
    // Diet of the ordered listing, same rules as kitchen summary and MealDesk:
    // listing diet, the only diet of the catalog, or the standard diet
    orderDietId(listingNodeIds) {
      const listingDiet = this.diets.find((d) => listingNodeIds.includes(d.nodeId));
      if (listingDiet) return listingDiet.nodeId;
      if (this.diets.length === 1) return this.diets[0].nodeId;

      const standard = this.diets.find((d) => ['standardowa', 'podstawowa']
        .includes((d.name || '').trim().toLowerCase()));
      return standard?.nodeId;
    },
    perPiece(price, currency) {
      return this.$t('shop.todayOrder.perPiece', [this.formatCurrency(price, currency)]);
    },
    resetCounts() {
      const counts = {};
      this.orders.forEach((o) => { counts[o.availabilityId] = o.count; });
      this.newItems.forEach((l) => { counts[l.id] = 0; });
      this.counts = counts;
    },
    startEdit() {
      this.resetCounts();
      this.editing = true;
    },
    cancelEdit() {
      this.resetCounts();
      this.editing = false;
    },
    save() {
      if (!this.hasChanges) return;

      this.pending = true;
      this.updateOrders({
        params: {
          query: {
            catalogId: this.catalogId,
          },
        },
        data: {
          addedItems: this.addedItems,
          cancelledItems: this.cancelledItems,
        },
      })
        .then(() => (this.addedItems.length > 0 ? this.deliverNewItems() : null))
        .then(() => {
          this.editing = false;
          this.$emit('updated');
        })
        .finally(() => {
          this.pending = false;
        });
    },
    // Orders placed at the counter are handed out right away
    deliverNewItems() {
      const today = moment().format('YYYY-MM-DD');
      return this.getOrders({
        params: {
          query: {
            from: today,
            to: moment().add(1, 'day').format('YYYY-MM-DD'),
            catalogId: this.catalogId,
            payerId: this.payer.id,
          },
        },
      })
        .then(({ data }) => Promise.all(data.orders
          .filter((o) => o.payerId === this.payer.id && o.day === today && o.delivered === 0)
          .map((o) => this.reportSingleDelivery({
            params: {
              query: {
                date: o.day,
                orderId: o.orderId,
                offerId: o.availabilityId,
                payerId: o.payerId,
                index: 0,
              },
            },
          }))));
    },
    requestPayerOffer() {
      if (this.offer.length === 0) return;

      this.findPayersOffer({
        params: {
          catalogId: this.catalogId,
        },
        data: {
          payerIds: [this.payer.id],
          productIds: [],
          listingIds: this.offer.map((l) => l.id),
          subscriptionsOnly: false,
        },
      })
        .then(({ data }) => {
          const plans = Array.isArray(data) ? data : (data?.offer || []);
          const plan = plans.find((x) => x.payerId === this.payer.id);
          this.payerListingIds = plan ? plan.listingIds : [];
          this.resetCounts();
        })
        .catch(() => {
          // Fallback to the full offer for today
          this.payerListingIds = null;
        });
    },
    report() {
      if (this.orders.length > 0) {
        this.reportDelivery({
          params: {
            query: {
              date: this.orders[0].day,
              payerId: this.payer.id,
            },
          },
        })
          .then(() => {
            this.$emit('delivered', this.payer.id);
          });
      }
    },
  },
  created() {
    this.resetCounts();
    this.requestPayerOffer();
  },
  mounted() {
    this.report();
  },
};
</script>

<style lang="scss" scoped>
.payer-orders {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.payer-header {
  gap: 12px;
}

.payer-name {
  font-size: 25px;
  font-weight: 600;
  margin-bottom: 12px;
}

.bracelet-btn {
  flex-shrink: 0;
  border: none;
  border-radius: 20px;
  background-color: $blue;
  color: white;
  font-weight: 500;
  padding: 8px 16px;
  white-space: nowrap;

  &:hover {
    background-color: darken($blue, 8%);
  }
}

.payer-properties {
  font-size: 14px;
  line-height: 1.9;

  .property-name {
    color: $light-gray;
    text-transform: uppercase;
    font-weight: 500;
  }
}

.divider {
  width: 100%;
  margin: 16px 0;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.edit-box {
  background-color: #f2f7ff;
  border: 1px solid #cfe2ff;
  border-radius: 10px;
  padding: 16px 12px 0;
}

.no-orders {
  background-color: #e1eeff;
  color: #0059cc;
  border-radius: 6px;
  padding: 16px;
  text-align: center;
  font-size: 16px;

  i {
    font-size: 45px;
    color: #a9cbff;
    margin-bottom: 4px;
  }

  .no-offer-info {
    font-size: 14px;
    margin-top: 4px;
  }
}

.product-set {
  background-color: #f2f7ff;
  border: 1px solid #cdd6e2;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 12px;
}

.product-set-name {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;

  &.crossed {
    text-decoration: line-through;
  }
}

.product-set {
  &.changed {
    border: 1px dashed $blue;
  }

  .product-set-header {
    gap: 12px;
    margin-bottom: 12px;

    .product-set-name {
      margin-bottom: 0;
    }
  }

  .statuses + .product-set-divider {
    margin: 16px 0 12px;
  }
}

.product {
  margin-bottom: 12px;

  .product-tag {
    display: inline-block;
    border: 1px solid $blue;
    color: #0059cc;
    border-radius: 15px;
    padding: 1px 12px;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  .product-description {
    font-size: 16px;
  }

  .product-plan-description {
    font-size: 14px;
    color: $light-gray;
  }
}

.statuses {
  gap: 6px;
}

.status {
  color: white;
  border-radius: 15px;
  padding: 2px 12px;
  font-size: 14px;
  font-weight: 500;

  &.green {
    background-color: $green;
  }

  &.red {
    background-color: $red;
  }
}

.actions {
  position: sticky;
  bottom: 0;
  display: flex;
  gap: 10px;
  margin-top: auto;
  padding: 12px 0;
  background-color: inherit;
  border-top: 1px solid #ddd;
}

.edit-box .actions {
  background-color: #f2f7ff;
}

.payer-orders > .actions {
  background-color: #fafafa;
  border-top: none;
}

.action-btn {
  flex: 1;
  height: 44px;
  border: none;
  border-radius: 22px;
  color: white;
  font-size: 18px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;

  &.cancel, &.edit {
    background-color: #9a9a9a;
  }

  &.confirm {
    background-color: $blue;
  }

  &:disabled {
    opacity: 0.6;
  }
}
</style>
